import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TreeItem } from "@atlaskit/tree/types";
import { TreeData } from "@atlaskit/tree";

import { setSelectedLayers } from "../../../ducks/project/project";
import { getSelectedLayers } from "../../../ducks/project/selectors";

export const useLayersTreeSelection = (tree: TreeData) => {
  const dispatch = useDispatch();
  const selectedLayers = useSelector(getSelectedLayers);
  const [selected, setSelected] = useState<string[]>([]);

  const items = useMemo(
    () => Object.keys(tree.items).reduce((result, current) => [...result, tree.items[current]], []),
    [tree.items],
  );

  const getItems = useCallback(
    (ids: string[]): TreeItem[] => ids?.map(id => items.find(item => item.id === id))?.filter(Boolean) || [],
    [items],
  );

  const getChildren = useCallback(
    (currentItems?: TreeItem[]): TreeItem[] => {
      if (!currentItems) return [];

      return currentItems.reduce(
        (result: TreeItem[], item: TreeItem) => [...result, item, ...getChildren(getItems(item.children as string[]))],
        [],
      );
    },
    [getItems],
  );

  const getIsSomeSelected = useCallback(
    (currentItems: TreeItem[]): boolean =>
      currentItems.some(({ id, children }) =>
        children?.length ? getIsSomeSelected(getItems(children as string[])) : selected.includes(id as string),
      ),
    [getItems, selected],
  );

  const getIsSomeDeselected = useCallback(
    (currentItems: TreeItem[]): boolean =>
      currentItems.some(({ id, children }) =>
        children?.length ? getIsSomeDeselected(getItems(children as string[])) : !selected.includes(id as string),
      ),
    [getItems, selected],
  );

  const getIsIndeterminate = useCallback(
    (currentItems?: TreeItem[]): boolean => {
      if (!currentItems?.length) return false;

      return getIsSomeSelected(currentItems) && getIsSomeDeselected(currentItems);
    },
    [getIsSomeDeselected, getIsSomeSelected],
  );

  const addItem = useCallback(
    ({ id, children }: TreeItem, newSelected: string[]): string[] =>
      children?.length || newSelected.includes(id as string) ? newSelected : [...newSelected, id as string],
    [],
  );

  const removeItem = useCallback((item: TreeItem, newSelected: string[]): string[] => {
    const result = [...newSelected];
    const index = newSelected?.indexOf(item.id as string);

    if (index >= 0) {
      result.splice(index, 1);
    }

    return result;
  }, []);

  const selectChildren = useCallback(
    (parent: TreeItem) => {
      let newSelected = [...selected];

      if (parent.children?.length) {
        getChildren(getItems(parent.children as string[])).forEach(item => {
          newSelected = addItem(item, newSelected);
        });
      } else {
        newSelected = addItem(parent, newSelected);
      }

      setSelected(newSelected);
    },
    [addItem, getChildren, getItems, selected],
  );

  const deselectChildren = useCallback(
    (parent: TreeItem) => {
      if (!selected) return;

      let newSelected = [...selected];

      if (parent.children?.length) {
        parent.children.forEach(child => {
          [child, ...getChildren(getItems([child as string])).map(({ id }) => id)].forEach(item => {
            newSelected = removeItem(getItems([item as string])[0], newSelected);
          });
        });
      } else {
        newSelected = removeItem(parent, newSelected);
      }

      setSelected(newSelected);
    },
    [getChildren, getItems, removeItem, selected],
  );

  const onSelect = useCallback(
    (parent: TreeItem) => {
      if (getIsSomeSelected([parent])) {
        deselectChildren(parent);
      } else {
        selectChildren(parent);
      }
    },
    [deselectChildren, getIsSomeSelected, selectChildren],
  );

  const isSelected = useCallback(
    (item: TreeItem) => getIsIndeterminate(getItems(item.children as string[])) || getIsSomeSelected([item]),
    [getIsIndeterminate, getIsSomeSelected, getItems],
  );

  useEffect(() => {
    dispatch(setSelectedLayers(selected));
  }, [selected]);

  useEffect(() => {
    if (!selected.length && selectedLayers.length) {
      setSelected(selectedLayers);
    }
  }, [selectedLayers]);

  return { selected: getItems(selected), isSelected, onSelect };
};
